/**
 * Finding nodes and links under the pointer, and where a link is drawn. Groups and bundles
 * have their own hit tests (`groupsOnScreen.ts`, `bundlesOnScreen.ts`).
 */
import { segmentDistance } from "./geometry";
import type { Scene } from "./index";
import type { Focus, Segment, SimLink, SimNode } from "./types";

/** Nodes and links under the pointer. */
export interface SceneHits {
  /** The visible node under a screen point (the one drawn on top). */
  hitNode(px: number, py: number): SimNode | null;
  /** The drawn link closest to a screen point (within a few pixels). Links hidden in a closed bundle are skipped. */
  hitLink(px: number, py: number, focus?: Focus): SimLink | null;
  /** Screen segment of a link, from the edge of one node to the edge of the other. */
  segment(l: SimLink): Segment | null;
}

/** The hit tests of `scene`. */
export function hits(scene: Scene): SceneHits {
  return {
    hitNode(px, py) {
      const order = scene.paintOrder();
      for (let i = order.length - 1; i >= 0; i--) {
        const n = order[i]!;
        if (Math.hypot(scene.sx(n) - px, scene.sy(n) - py) <= scene.rs(n) + 4) return n;
      }
      return null;
    },

    hitLink(px, py, focus) {
      let best: SimLink | null = null;
      let bestDistance = 7;
      // In 2D, a link whose box (in world units) is not within reach of the pointer is skipped
      // before anything is projected: a big graph has 80 000 links, met on every pointer move.
      const flatView = scene.dimension === "2d";
      const k = scene.view.k;
      const wx = (px - scene.view.x) / k;
      const wy = (py - scene.view.y) / k;
      // The reach of a hit in world units (a drawn link lies inside the box of its two nodes' centres).
      const pad = (bestDistance + 1) / k;
      for (const l of scene.links) {
        if (flatView && (wx < Math.min(l.a.x, l.b.x) - pad || wx > Math.max(l.a.x, l.b.x) + pad || wy < Math.min(l.a.y, l.b.y) - pad || wy > Math.max(l.a.y, l.b.y) + pad)) continue;
        if (!l.visible || (focus && !scene.linkDrawn(l, focus))) continue;
        const s = scene.segment(l);
        if (!s) continue;
        const d = segmentDistance(px, py, s.x1, s.y1, s.x2, s.y2);
        if (d < bestDistance) {
          bestDistance = d;
          best = l;
        }
      }
      return best;
    },

    segment(l) {
      const ax = scene.sx(l.a);
      const ay = scene.sy(l.a);
      const bx = scene.sx(l.b);
      const by = scene.sy(l.b);
      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.hypot(dx, dy);
      if (len < 1) return null;
      const ux = dx / len;
      const uy = dy / len;
      const ra = scene.rs(l.a) + 1;
      const rb = scene.rs(l.b) + 2;
      if (len < ra + rb + 4) return null;
      return { x1: ax + ux * ra, y1: ay + uy * ra, x2: bx - ux * rb, y2: by - uy * rb };
    },
  };
}
